import { P2GButton, P2GDialog } from '@parcel2go/core-ui';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import RequirementsField, { RequirementsFieldProps } from '~/components/core/forms/form-field-with-named-requirement';
import useParcelValidation from '~/hooks/use-parcel-validation';
import { TranslationNamespaces } from '~/lib/constants';
import { QuoteParcel, QuoteRequirementSection } from '~/lib/types/basket-types';

const SearchParcelHistory = lazy(() => import('./algolia'));

const defaultTitle = (restriction: string | undefined) => {
  switch (restriction) {
    case 'More Information':
      return 'More Information Requested';

    default:
      return 'Prohibited Item Alert';
  }
};

const defaultCopy = (restriction: string | undefined) => {
  switch (restriction) {
    case 'No Compensation':
      return (
        `We can carry "{{contents}}" but it will not be protected for damage or loss and it is sent at your own risk.` +
        `(See our No Protection List). If the item is lost within the network we will refund the cost of the carriage only.`
      );

    case 'Conditional Cariage':
      return `"{{contents}}" are not protected for damages and are only protected for loss where Parcel Protection is taken. See our Items Protected for Loss Only list.`;

    case 'More Information':
      return `The contents description "{{contents}}" is unfortunately too vague for our services, please add a more specific description of the item.`;

    default:
      return restriction;
  }
};

type ContentsSummaryProps = {
  parcel: QuoteParcel;
  onChange: (parcel: QuoteParcel) => void;
  loading: boolean;
  requirements: QuoteRequirementSection;
  errors: Record<string, string>;
  index: number;
  translationKey: string;
};

export default function ContentsSummary(props: ContentsSummaryProps) {
  const { isAuthenticated } = useAuth();
  const { track } = useAnalytics();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const [contents, setContents] = useState(props.parcel.contentSummary || undefined);
  const { prohibited, noProtection, moreInformation, match, type, conditional } = useParcelValidation({
    contents: contents
  });
  const [showProhibitionModal, setShowProhibitionModal] = useState(false);

  useEffect(() => {
    if (contents) {
      track({ type: 'parcel_contents_changed', payload: { index: props.index, contents }, persist: false });
    }
  }, [contents, props.index, track]);

  useEffect(() => {
    if (contents && prohibited) {
      track({ type: 'parcel_contents_prohibited', payload: { index: props.index, contents, match } });
    }
  }, [prohibited, contents, match, track, props.index]);

  const fieldProps = useMemo(() => {
    const p: RequirementsFieldProps = {
      defaultLabel: t(props.translationKey, { defaultValue: 'Parcel Contents' }),
      defaultRequired: true,
      dataTestId: `txt-parcel-${props.index}-content`,
      id: `txt-${props.parcel.id}-content`,
      requirements: props.requirements,
      translationKey: props.translationKey,
      name: 'contentsummary',
      data: props.parcel.contentSummary || '',
      update: (value) => props.onChange({ ...props.parcel, contentSummary: value }),
      loading: props.loading,
      errors: props.errors,
      onBlur: (e) => setContents(e.currentTarget.value),
      autoComplete: 'off',
      forcedError: prohibited
        ? t('PARCEL_CONTENTS_PROHIBITED', {
            contents,
            defaultValue: `We cannot send "{{contents}}" as it is on the list of prohibited items.`
          })
        : undefined,
      unmasked: true,
      inputProps: prohibited
        ? {
            'data-custom-val-error': t('PARCEL_CONTENTS_PROHIBITED', {
              contents,
              defaultValue: `We cannot send "{{contents}}" as it is on the list of prohibited items.`
            })
          }
        : undefined
    };
    return p;
  }, [contents, prohibited, props, t, setContents]);

  useEffect(
    () => setShowProhibitionModal(noProtection || moreInformation || conditional),
    [moreInformation, noProtection, conditional]
  );

  return (
    <>
      {isAuthenticated && (
        <Suspense>
          <SearchParcelHistory
            {...fieldProps}
            onSelect={(p) => props.onChange({ ...props.parcel, ...p })}
            fallback={<RequirementsField {...fieldProps} />}
          />
        </Suspense>
      )}
      {!isAuthenticated && <RequirementsField {...fieldProps} />}
      <P2GDialog
        dataTestId="parcel-contents-prohibited-modal"
        title={t(`PARCEL_CONTENTS_PROHIBITED_${type?.toUpperCase()}_TITLE`, {
          contents,
          defaultValue: defaultTitle(type)
        })}
        content={t(`PARCEL_CONTENTS_PROHIBITED_${type?.toUpperCase()}_CONTENT`, {
          contents,
          defaultValue: defaultCopy(type)
        })}
        open={showProhibitionModal}
        actions={[
          <P2GButton
            onClick={() => setShowProhibitionModal(false)}
            text={t('PARCEL_CONTENTS_PROHIBITED_ACCEPT_WARNING', { defaultValue: 'I Accept' })}
            key="accept-prohibition-warning"
            variant="primary"
          />
        ]}
      />
    </>
  );
}
